import _extends from '@babel/runtime/helpers/extends';
import React__default from 'react';
import styled, { css } from 'styled-components';
import PT from 'prop-types';
import { $ as $f7dceffc5ad7768b$export$4e328f61c538687f } from '../3rdPartyModules/_react-aria/focus/dist/useFocusRing-BO5gj-B1.js';
import { $ as $3ef42575df84b30b$export$9d1611c77c2fe928 } from '../3rdPartyModules/_react-aria/utils/dist/mergeProps-DMtmc2Zw.js';
import { L as LoadingIcon } from '../Button/Button-D3DP5nr7.js';
import { f as focusOutline } from '../FocusIndicator/styleMixins/focusOutline-DNwtedBp.js';
import { i as isMobile } from '../theme/style-mixins/isMobile/isMobile-CGsYoba7.js';
import { T as Text } from '../Text/Text-nnmbpLTK.js';
import { I as Icon } from '../Icon/Icon-CpzUnHit.js';
import { t as times } from '../LocalModules/storm-ui-icons/dist/esm/localIcons-EM4gOTGb-B7bhIt-r.js';
import { u as useTaktId } from '../TaktIdContext/TaktIdContext-DlOuPWuU.js';
import { c as createStormTaktId } from '../TaktIdContext/TaktIdContext.utils-DaCk9T1Y.js';

const isSmallSize = size => size === 'small';
const borderRadius = ({
  theme
}) => theme.pill.borderRadius;
const endCapPadding = ({
  size,
  theme
}) => isSmallSize(size) ? theme.pill.small.paddingHorizontal : theme.pill.paddingHorizontal;
const bgColor = ({
  $loading,
  disabled,
  applied,
  theme
}) => {
  switch (true) {
    case $loading:
      return theme.pill.disabled.bgColor;
    case disabled:
      return theme.pill.disabled.bgColor;
    case applied:
      return theme.pill.applied.bgColor;
    default:
      return theme.pill.bgColor;
  }
};
const textColor = ({
  $loading,
  disabled,
  theme
}) => {
  switch (true) {
    case $loading:
      return theme.pill.disabled.textColor;
    case disabled:
      return theme.pill.disabled.textColor;
    default:
      return theme.pill.textColor;
  }
};
const desktopHeight = ({
  size,
  theme
}) => isSmallSize(size) ? theme.pill.small.height : theme.pill.height;
const mobileHeight = ({
  size,
  theme
}) => isSmallSize(size) ? theme.pill.mobile.small.height : theme.pill.height;
const selectWidth = ({
  fullWidth,
  maxWidth,
  theme
}) => {
  if (fullWidth) {
    /**
     * there is an issue with flex children shrinking beyond their content.
     * to fix this, when `fullWidth` is set, we set a "min-width: 0" on flexed child elements
     */
    return 'min-width: 0';
  }
  if (typeof maxWidth === 'number') {
    return `max-width: ${maxWidth}px`;
  }
  return '';
};
const PillText = /*#__PURE__*/styled(Text).attrs(props => ({
  ...props,
  type: 'span'
})).withConfig({
  componentId: "sc-storm-ui-30060407__sc-minkvh-0"
})(["display:block;color:inherit;line-height:1.2;"]);
const PillFrame = /*#__PURE__*/styled('div').withConfig({
  componentId: "sc-storm-ui-30060407__sc-minkvh-1"
})(["display:inline-flex;flex-direction:row;flex-wrap:nowrap;justify-content:flex-start;align-content:stretch;align-items:stretch;min-height:", ";background:", ";isolation:isolate;", " ", " /*! @noflip */ border-top-right-radius:", ";/*! @noflip */ border-bottom-right-radius:", ";/*! @noflip */ border-top-left-radius:", ";/*! @noflip */ border-bottom-left-radius:", ";> *{color:", ";border:none;margin:0;background:", ";padding:0 0.25em;}> *:first-child{padding-inline-start:", ";/*! @noflip */ border-top-left-radius:", ";/*! @noflip */ border-bottom-left-radius:", ";}> *:last-child{padding-inline-end:", ";/*! @noflip */ border-top-right-radius:", ";/*! @noflip */ border-bottom-right-radius:", ";}/*! @noflip */ [dir=\"rtl\"] &&{> *:first-child{/*! @noflip */ border-top-left-radius:0;/*! @noflip */ border-bottom-left-radius:0;/*! @noflip */ border-top-right-radius:", ";/*! @noflip */ border-bottom-right-radius:", ";}> *:last-child{/*! @noflip */ border-top-right-radius:0;/*! @noflip */ border-bottom-right-radius:0;/*! @noflip */ border-top-left-radius:", ";/*! @noflip */ border-bottom-left-radius:", ";}> *:first-child:last-child{/*! @noflip */ border-top-right-radius:", ";/*! @noflip */ border-bottom-right-radius:", ";/*! @noflip */ border-top-left-radius:", ";/*! @noflip */ border-bottom-left-radius:", ";}}"], desktopHeight, bgColor, ({
  theme,
  $loading,
  disabled,
  $displayOnly
}) => !$loading && !disabled && !$displayOnly && `
    :hover {
      background: ${theme.pill.hover.bgColor};
      box-shadow: ${theme.pill.hover.boxShadow};
    }
  `, isMobile(css(["min-height:", ";"], mobileHeight)), borderRadius, borderRadius, borderRadius, borderRadius, textColor, bgColor, endCapPadding, borderRadius, borderRadius, endCapPadding, borderRadius, borderRadius, borderRadius, borderRadius, borderRadius, borderRadius, borderRadius, borderRadius, borderRadius, borderRadius);
PillFrame.displayName = 'PillFrame';
const NonClickableContent = /*#__PURE__*/styled('div').withConfig({
  componentId: "sc-storm-ui-30060407__sc-minkvh-2"
})(["flex:1 1 auto;", ";display:flex;flex-direction:row;flex-wrap:nowrap;justify-content:center;align-content:center;align-items:center;"], selectWidth);
const ClickableContent = /*#__PURE__*/styled('button').attrs(props => {
  const {
    isFocusVisible,
    focusProps
  } = $f7dceffc5ad7768b$export$4e328f61c538687f();
  return $3ef42575df84b30b$export$9d1611c77c2fe928(props, focusProps, {
    $focusVisible: isFocusVisible,
    type: 'button'
  });
}).withConfig({
  componentId: "sc-storm-ui-30060407__sc-minkvh-3"
})(["flex:1 1 auto;", ";display:flex;flex-direction:row;flex-wrap:nowrap;justify-content:center;align-content:center;align-items:center;outline:none;cursor:", ";:focus{", "}"], selectWidth, ({
  disabled,
  $loading,
  onClick
}) => {
  if (disabled) return 'not-allowed';
  if ($loading) return 'not-allowed';
  return onClick ? 'pointer' : 'default';
}, ({
  $focusVisible = false
}) => $focusVisible && css(["", " z-index:1;"], focusOutline));
const Content = ({
  disabled,
  title,
  maxWidth,
  fullWidth,
  onClick,
  applied,
  taktId,
  taktValue,
  loading,
  ...rest
}) => {
  const {
    getDataTaktAttributes
  } = useTaktId({
    taktId,
    fallbackId: createStormTaktId('pill')
  });
  if (onClick) {
    return /*#__PURE__*/React__default.createElement(ClickableContent, _extends({}, getDataTaktAttributes({
      taktValue
    }), rest, {
      disabled: disabled,
      maxWidth: maxWidth,
      fullWidth: fullWidth,
      title: title,
      onClick: loading ? undefined : onClick,
      "aria-pressed": applied,
      $loading: loading
    }, loading && {
      'aria-live': 'assertive'
    }, loading && {
      'aria-disabled': true
    }));
  }
  return /*#__PURE__*/React__default.createElement(NonClickableContent, _extends({}, rest, {
    disabled: disabled,
    maxWidth: maxWidth,
    fullWidth: fullWidth,
    title: title
  }, loading && {
    'aria-live': 'assertive'
  }));
};
Content.propTypes = {
  title: PT.string,
  applied: PT.bool,
  disabled: PT.bool,
  fullWidth: PT.bool,
  maxWidth: PT.number,
  onClick: PT.func,
  tabIndex: PT.number,
  loading: PT.bool
};
Content.defaultProps = {
  title: undefined,
  applied: true,
  disabled: false,
  fullWidth: false,
  maxWidth: undefined,
  onClick: undefined,
  tabIndex: undefined,
  loading: false
};
const LabelText = /*#__PURE__*/styled(PillText).withConfig({
  componentId: "sc-storm-ui-30060407__sc-minkvh-4"
})(["overflow:hidden;text-overflow:ellipsis;white-space:nowrap;"]);
LabelText.displayName = 'LabelText';
const PrefixText = /*#__PURE__*/styled(PillText).withConfig({
  componentId: "sc-storm-ui-30060407__sc-minkvh-5"
})(["white-space:nowrap;margin-inline-end:", ";font-weight:bold;"], ({
  theme
}) => theme.pill.prefixPaddingEnd);
PrefixText.displayName = 'PrefixText';
const SuffixText = /*#__PURE__*/styled(PillText).withConfig({
  componentId: "sc-storm-ui-30060407__sc-minkvh-6"
})(["white-space:nowrap;margin-inline-start:", ";font-weight:bold;"], ({
  theme
}) => theme.pill.suffixPaddingStart);
SuffixText.displayName = 'SuffixText';
const Pill = ({
  label,
  prefix,
  suffix,
  size = 'base',
  applied,
  disabled = false,
  fullWidth = false,
  maxWidth = 500,
  className = '',
  onClick,
  onClose,
  closeLabel = 'remove',
  tabIndex,
  taktId,
  taktValue,
  loading,
  loadingLabel,
  ...rest
}) => /*#__PURE__*/React__default.createElement(PillFrame, _extends({}, rest, {
  size: size,
  className: className,
  disabled: disabled,
  applied: applied,
  $displayOnly: typeof onClick !== 'function' && typeof onClose !== 'function' || loading,
  $loading: loading
}), /*#__PURE__*/React__default.createElement(Content, {
  taktId: taktId,
  taktValue: taktValue,
  disabled: disabled,
  title: label,
  maxWidth: maxWidth,
  fullWidth: fullWidth,
  onClick: onClick,
  loading: loading
}, loading && /*#__PURE__*/React__default.createElement(LoadingIcon, null), loading && loadingLabel ? /*#__PURE__*/React__default.createElement(LabelText, null, loadingLabel) : /*#__PURE__*/React__default.createElement(React__default.Fragment, null, prefix && /*#__PURE__*/React__default.createElement(PrefixText, null, prefix, label ? ':' : ''), /*#__PURE__*/React__default.createElement(LabelText, null, label), suffix && /*#__PURE__*/React__default.createElement(SuffixText, null, suffix))), onClose && /*#__PURE__*/React__default.createElement(ClickableContent, {
  disabled: loading || disabled,
  onClick: onClose,
  tabIndex: tabIndex
}, /*#__PURE__*/React__default.createElement(Text, {
  type: "sr-only"
}, closeLabel), /*#__PURE__*/React__default.createElement(Icon, {
  type: times,
  "aria-hidden": true
})));
Pill.propTypes = {
  label: PT.string,
  prefix: PT.string,
  suffix: PT.string,
  size: PT.oneOf(['base', 'small']),
  applied: PT.bool,
  disabled: PT.bool,
  fullWidth: PT.bool,
  maxWidth: PT.number,
  className: PT.string,
  onClick: PT.func,
  onClose: PT.func,
  closeLabel: PT.string,
  /**
   * To indicate the button is loading.
   */
  loading: PT.bool,
  /**
   * Substitute for Button's body when loading is true.
   */
  loadingLabel: PT.string
};
Pill.defaultProps = {
  label: undefined,
  prefix: undefined,
  suffix: undefined,
  size: 'base',
  applied: true,
  disabled: false,
  fullWidth: false,
  maxWidth: undefined,
  closeLabel: 'remove',
  className: '',
  onClick: undefined,
  onClose: undefined,
  loading: false,
  loadingLabel: undefined
};
Pill.displayName = 'Pill';

export { Pill as P };
//# sourceMappingURL=Pill-B5s3psMP.js.map
